import type { CampaignImage, CampaignVideo, Illustration, IllustrationSlotName, Skin } from '@/libs/campaigns/types';
import type { VideoSource } from '@/utils/preloadVideoSources';
import gameNeedsPurchasedScMobile from '@/public/assets/image/games/disallow-play-game/disallow-play-game2x.webp';
import gameNeedsPurchasedScDesktop from '@/public/assets/image/games/disallow-play-game/disallow-play-game.webp';
import emptyStateSmall from '@/public/assets/image/global-states/empty/default/empty-default-state-small@3x.webp';
import emptyState from '@/public/assets/image/global-states/empty/default/empty-default-state@3x.webp';
import duplicateAccount from '@/public/assets/image/kyc/kyc-duplicate-rejection.webp';
import infoBannerDesktop from '@/public/assets/image/purchase_banner/default/main-picture-desktop.webp';
import infoBannerMobile from '@/public/assets/image/purchase_banner/default/main-picture-mobile.webp';
import infoBannerTablet from '@/public/assets/image/purchase_banner/default/main-picture-tablet.webp';
import freeGoldCoinChest from '@/public/assets/image/register/default/congrats-chest.webp';

export const imageIllustration = (image: CampaignImage): Illustration => ({ type: 'image', image });

/** An export of the whole design frame (background, border and corners included). */
export const framedImageIllustration = (image: CampaignImage): Illustration => ({ type: 'image', image, framed: true });

export const isFramedIllustration = (illustration: Illustration | null): boolean => (
  illustration?.type === 'image' && illustration.framed === true
);

export const videoIllustration = (video: CampaignVideo): Illustration => ({ type: 'video', video });

const registrationCongratulations = videoIllustration({
  webm: '/videos/auth/congratulation-registeration/congrats-registration.webm',
  mov: '/videos/auth/congratulation-registeration/congrats-registration-ios.mov',
});

const successTiger = videoIllustration({
  webm: '/videos/global-state/tiger-states/success/success-tiger.webm',
  mov: '/videos/global-state/tiger-states/success/success-tiger-ios.mov',
});

const failedTiger = videoIllustration({
  webm: '/videos/global-state/tiger-states/failed/failed-tiger.webm',
  mov: '/videos/global-state/tiger-states/failed/failed-tiger-ios.mov',
});

/** What each slot shows without an Active Campaign. `null` shows nothing (decoration only a Campaign adds). */
export const ILLUSTRATION_SLOT_DEFAULTS = {
  lobbyInfoBannerDesktop: imageIllustration(infoBannerDesktop),
  lobbyInfoBannerTablet: imageIllustration(infoBannerTablet),
  lobbyInfoBannerMobile: imageIllustration(infoBannerMobile),
  registrationCongratulations,
  freeGoldCoinChest: imageIllustration(freeGoldCoinChest),
  emptyState: imageIllustration(emptyState),
  emptyStateCompact: imageIllustration(emptyStateSmall),
  emptyFavorites: imageIllustration(emptyState),
  notFoundGarland: null,
  duplicateAccount: imageIllustration(duplicateAccount),
  spendingLimitReached: failedTiger,
  redemptionSubmitted: successTiger,
  redemptionFailed: failedTiger,
  gameNeedsPurchasedScDesktop: imageIllustration(gameNeedsPurchasedScDesktop),
  gameNeedsPurchasedScMobile: imageIllustration(gameNeedsPurchasedScMobile),
} as const satisfies Record<IllustrationSlotName, Illustration | null>;

export const ILLUSTRATION_SLOT_NAMES = Object.keys(ILLUSTRATION_SLOT_DEFAULTS) as IllustrationSlotName[];

/** The Skin's art for the slot, or the slot's default. */
export function resolveIllustration(skin: Skin | null, slot: IllustrationSlotName): Illustration | null {
  return skin?.illustrations?.[slot] ?? ILLUSTRATION_SLOT_DEFAULTS[slot];
}

/** The video sources an illustration plays (MOV for iOS first), e.g. to preload them; none for a still. */
export function illustrationVideoSources(illustration: Illustration | null): VideoSource[] {
  const videos: CampaignVideo[] = [];

  if (illustration?.type === 'video') {
    videos.push(illustration.video);
  } else if (illustration?.type === 'layers') {
    for (const layer of illustration.layers) {
      if (layer.kind === 'video') {
        videos.push(layer.video);
      }
    }
  }

  return videos.flatMap(video => [
    { src: video.mov, type: 'video/quicktime' },
    { src: video.webm, type: 'video/webm' },
  ]);
}
